/* src/world/tileManager.ts — visible-tile streaming, eviction, and origin rebase.
 *
 * Two instances of this class now run at once — the LOD ladder PLAN.md Part 2 §4 always
 * anticipated: a z15 street ring around the camera and a z13 skyline ring reaching tens of
 * kilometres, each with its own zoom, view range, budget and vertical offset. The instance
 * knows nothing about the other; worldView owns the policy of when the far ring is visible.
 */

import type { BufferGeometry, Object3D, PerspectiveCamera } from 'three';
import { Group, Mesh, Vector3 } from 'three';
import { emit } from '../core/events.ts';
import { mercToLocal, type Origin } from '../map/originRebase.ts';
import {
  formatChunkKey,
  lonLatToTile,
  tileBoundsLonLat,
  tileOriginMerc,
  tileSizeMerc,
  unproject,
  type TileCoord,
} from '../map/tileMath.ts';
import { localToMerc } from '../map/originRebase.ts';
import type { TilePipeline, TileResult } from '../map/tilePipeline.ts';
import { createLayerMesh, setTilePosition, type TileObject } from '../render/tileMesh.ts';
import type { LayerMaterials } from '../render/materials.ts';
import type { PackedPaths } from '../workers/protocol.ts';

export const TILE_ZOOM = 15;

/** Extra ring of tiles kept beyond the visible set, so a small pan-and-return refetches nothing. */
const HYSTERESIS_TILES = 1;
/**
 * Concurrent tile loads. Every un-surveyed tile is now a two-hop fetch (game DB, then the
 * continent basemap), so keeping more requests in flight is what hides that latency; the
 * decode/build workers pipeline behind it either way.
 */
const MAX_CONCURRENT = 12;

interface TileEntry {
  readonly key: string;
  readonly coord: TileCoord;
  object: TileObject | null;
  state: 'loading' | 'ready' | 'empty' | 'failed';
  lastSeen: number;
  /** cos(origin lat) when this tile's geometry was built; drives the placement scale ratio. */
  builtScale: number;
  /** Whether the built tile contains any building geometry — the survey gate reads this. */
  hasBuildings: boolean;
  /** Bumped by invalidateRegion. A load result carrying a stale gen is discarded (and the
   * tile refetched), so a survey landing mid-flight can never stick stale content. */
  gen: number;
  /** A fetch for this tile is currently in the pipeline (network + decode + build). */
  inFlight: boolean;
  /** Road centrelines for the ambient sim, kept beside the meshes they were built with. */
  paths?: PackedPaths;
}

export interface TileManagerOptions {
  /** Parent for tile groups — the scene for the street ring, a toggleable Group for the far ring. */
  readonly scene: Object3D;
  readonly pipeline: TilePipeline;
  readonly materials: LayerMaterials;
  readonly tileUrl: (z: number, x: number, y: number) => string;
  /** The zoom this ring streams at. */
  readonly zoom: number;
  /** Ground distance (m) this ring loads out to, given the camera's current far plane. */
  readonly maxView: (camera: PerspectiveCamera) => number;
  /** Hard cap on simultaneously resident tiles. Bounds both memory and draw calls. */
  readonly maxResident: number;
  /** Vertical offset for every tile — the far ring sits fractionally below the street ring. */
  readonly yOffset?: number;
  /** Only the street ring reports boot progress; a second reporter would fight it. */
  readonly announce?: boolean;
  /**
   * Whether this ring's meshes serve click raycasts. Pickable geometry keeps its CPU-side
   * positions/index/feature ids; everything else frees its JS arrays once the GPU has them —
   * roughly half the memory of a ring (tileMesh.ts).
   */
  readonly pickable?: boolean;
  /**
   * Painter's-order stage for this ring's depthless flat layers (tileMesh.ts FLAT_ORDER).
   * The far ring paints its ground before the street ring paints over it.
   */
  readonly orderBias?: number;
  /**
   * Draw stage for this ring's depth-tested solids. The far ring draws AFTER the street
   * ring so its duplicate of the city under the near tiles is early-z rejected instead of
   * shaded and painted over — measured at ~7 ms/frame at retina density (tileMesh.ts).
   */
  readonly solidOrder?: number;
  /**
   * Whether this ring participates in the shadow system at all. The far ring opts out:
   * the sun's frustum never leaves the street ring, so far meshes can neither throw a
   * visible shadow nor stand in one — but as casters they were re-drawn into the shadow
   * map on every update, and as receivers every pixel paid the PCF lookup (tileMesh.ts).
   */
  readonly shadows?: boolean;
}

/**
 * GPU uploads happen the first frame a mesh renders, and a burst of tile arrivals uploaded
 * in one frame is a visible hitch mid-pan. Arrivals therefore queue, and each frame admits
 * tiles until a BYTE budget is spent, not a count — two suburban tiles are nothing, one CBD
 * monster is a frame's worth on its own. The first tile always commits regardless of size
 * (a budget can only defer a stall, not shrink it).
 */
const COMMIT_BYTES_PER_FRAME = 6_000_000;

function packedBytes(m: TileResult['meshes'][number]): number {
  return (
    m.positions.byteLength + m.normals.byteLength + m.colors.byteLength +
    m.uvs.byteLength + m.indices.byteLength + m.featureIds.byteLength +
    (m.texLayer?.byteLength ?? 0)
  );
}

/** A tile mid-commit: its group is already placed in the scene, its meshes join it a
 * budget's worth per frame (the worker chunks dense building layers to make that possible). */
interface InProgress {
  readonly key: string;
  readonly result: TileResult;
  /** The entry's gen when this result arrived; a mismatch mid-commit aborts the job. */
  readonly gen: number;
  readonly geometries: BufferGeometry[];
  meshIndex: number;
  triangles: number;
}

export class TileManager {
  readonly #tiles = new Map<string, TileEntry>();
  readonly #queue: TileCoord[] = [];
  readonly #arrivals: Array<{ key: string; result: TileResult; gen: number }> = [];
  #loading = 0;
  #frame = 0;
  #origin: Origin;
  #announcedTotal = 0;
  #announcedDone = 0;
  /** Monotonic count of meshes entering or leaving the scene — the shadow pass only needs
   * re-rendering on frames where this moved (worldView owns that policy). */
  #mutations = 0;

  constructor(private readonly opts: TileManagerOptions, origin: Origin) {
    this.#origin = origin;
  }

  get residentCount(): number {
    return this.#tiles.size;
  }

  get readyCount(): number {
    let n = 0;
    for (const t of this.#tiles.values()) if (t.state === 'ready' || t.state === 'empty') n += 1;
    return n;
  }

  get triangles(): number {
    let n = 0;
    for (const t of this.#tiles.values()) n += t.object?.triangles ?? 0;
    return n;
  }

  /** Tiles waiting to enter the scene (or mid-entry) — "is streaming visibly under way". */
  get pendingCommits(): number {
    return this.#arrivals.length + (this.#inProgress ? 1 : 0);
  }

  get mutations(): number {
    return this.#mutations;
  }

  /** Diagnostics for the perf probes: where work is sitting in the pipeline right now. */
  get queuedCount(): number {
    return this.#queue.length;
  }

  get loadingCount(): number {
    return this.#loading;
  }

  /**
   * Road centrelines from every resident tile with roads, each with its group so the ambient
   * sim can read the tile's current local position (rebase-safe: groups are re-placed by
   * setOrigin, so a path point plus its group's position is always current local metres).
   */
  ambientSources(): Array<{ key: string; group: Object3D; paths: PackedPaths }> {
    const out: Array<{ key: string; group: Object3D; paths: PackedPaths }> = [];
    for (const entry of this.#tiles.values()) {
      if (entry.state === 'ready' && entry.object && entry.paths) {
        out.push({ key: entry.key, group: entry.object.group, paths: entry.paths });
      }
    }
    return out;
  }

  /**
   * Re-derive every tile's position from its own mercator origin. Positions are recomputed
   * from source-of-truth coordinates, never shifted by a delta — shifting accumulates error
   * with each rebase, which is the bug the floating origin exists to prevent.
   */
  setOrigin(origin: Origin): void {
    this.#origin = origin;
    for (const entry of this.#tiles.values()) {
      if (entry.object) this.#place(entry);
    }
  }

  update(
    camera: PerspectiveCamera,
    biasX = 0,
    biasZ = 0,
    budget: { bytes: number } = { bytes: COMMIT_BYTES_PER_FRAME },
  ): void {
    this.#frame += 1;

    const visible = visibleTiles(
      camera, this.#origin, this.opts.zoom, this.opts.maxView(camera), this.opts.maxResident,
      biasX, biasZ,
    );
    for (const coord of visible) {
      const key = formatChunkKey(coord.z, coord.x, coord.y);
      const existing = this.#tiles.get(key);
      if (existing) {
        existing.lastSeen = this.#frame;
        continue;
      }
      this.#tiles.set(key, {
        key, coord, object: null, state: 'loading', lastSeen: this.#frame,
        builtScale: this.#origin.scale, hasBuildings: false, gen: 0, inFlight: false,
      });
      this.#queue.push(coord);
      this.#announcedTotal += 1;
    }

    /*
     * Nearest-first: fill the ground under the camera before the edges of the view. The
     * visible set arrives in row-major order, which on a fresh arrival painted a corner of
     * the screen first and left the centre green — the exact "nothing is happening" feeling.
     */
    if (this.#queue.length > 1) {
      const eye = camera.position;
      const half = tileSizeMerc(this.opts.zoom) / 2;
      const dist = (c: TileCoord): number => {
        const [mx, my] = tileOriginMerc(c.z, c.x, c.y);
        const [x, z] = mercToLocal(mx + half, my - half, this.#origin);
        return Math.hypot(x - eye.x, z - eye.z);
      };
      this.#queue.sort((a, b) => dist(a) - dist(b));
    }

    this.#pump();
    this.#commit(budget);
    this.#evict(visible);
  }

  #inProgress: InProgress | null = null;

  /** Admit finished tile MESHES into the scene until the frame's upload budget is spent.
   * The budget object is SHARED across every ring worldView updates this frame — two rings
   * each spending a private budget is how zoom-out (both streaming) doubled the burst.
   * A tile's group appears immediately and its layers stream into it; the tile only counts
   * as ready (pickable, survey-relevant, path-serving) once every mesh is in. */
  #commit(budget: { bytes: number }): void {
    while (budget.bytes > 0) {
      if (!this.#inProgress) {
        const arrival = this.#arrivals.shift();
        if (!arrival) return;
        const start = this.#tiles.get(arrival.key);
        // Evicted while waiting its turn — or invalidated, in which case this result is
        // stale and the fresh fetch (already queued) owns the slot.
        if (!start || start.state !== 'loading' || start.gen !== arrival.gen) continue;
        // The swap moment for a re-fetched tile: stale geometry leaves only now that its
        // replacement is decoded, built, and about to stream in — never seconds earlier.
        this.#dropObject(start);
        const group = new Group();
        group.matrixAutoUpdate = false;
        const geometries: BufferGeometry[] = [];
        start.object = {
          group,
          geometries,
          triangles: 0,
          dispose(): void {
            for (const g of geometries) g.dispose();
            group.clear();
          },
        };
        start.builtScale = arrival.result.metreScale;
        this.#place(start);
        this.opts.scene.add(group);
        this.#inProgress = {
          key: arrival.key, result: arrival.result, gen: arrival.gen,
          geometries, meshIndex: 0, triangles: 0,
        };
      }

      const job = this.#inProgress;
      const live = this.#tiles.get(job.key);
      if (!live || live.state !== 'loading' || !live.object || live.gen !== job.gen) {
        // Evicted (or invalidated) mid-commit; the partial object is either disposed
        // already (#drop) or will be swapped out when the fresh arrival starts.
        this.#inProgress = null;
        continue;
      }

      while (budget.bytes > 0 && job.meshIndex < job.result.meshes.length) {
        const packed = job.result.meshes[job.meshIndex];
        job.meshIndex += 1;
        if (!packed) continue;
        budget.bytes -= packedBytes(packed);
        const built = createLayerMesh(
          packed, this.opts.materials, this.opts.pickable ?? true,
          (this.opts.orderBias ?? 0) + flatTieBreak(live.coord),
          this.opts.solidOrder ?? 0,
          this.opts.shadows ?? true,
        );
        if (!built) continue;
        live.object.group.add(built.mesh);
        job.geometries.push(built.geometry);
        job.triangles += built.triangles;
        this.#mutations += 1;
      }

      if (job.meshIndex >= job.result.meshes.length) {
        live.state = 'ready';
        live.object.triangles = job.triangles;
        live.hasBuildings = job.result.meshes.some((m) => m.layer === 'buildings');
        if (job.result.paths) live.paths = job.result.paths;
        this.#inProgress = null;
      }
    }
  }

  /**
   * Is the ground at local point (x, z) currently drawn by a fully-committed tile of this
   * ring? True only for a 'ready' tile with real geometry, or a soft-swap still showing its
   * complete previous geometry. 'empty' is deliberately NOT covering: an empty street tile
   * means the z15 source has nothing there, but the far ring's z12 source may legitimately
   * disagree — culling its buildings against emptiness is how "buildings disappear when I
   * zoom in" happened. Mid-commit and still-loading tiles are not covering either, so the
   * far ring keeps bridging gaps exactly while gaps exist.
   */
  coversPoint(x: number, z: number): boolean {
    const [mx, my] = localToMerc(x, z, this.#origin);
    const [lon, lat] = unproject(mx, my);
    const t = lonLatToTile(lon, lat, this.opts.zoom);
    const entry = this.#tiles.get(formatChunkKey(this.opts.zoom, t.x, t.y));
    if (!entry || entry.object === null) return false;
    if (entry.state === 'ready') return true;
    return entry.state === 'loading' && this.#inProgress?.key !== entry.key;
  }

  /**
   * Hide this ring's building chunks wherever `probe` says another ring is already drawing
   * that ground in full detail — rasterising the far ring's copy of a CBD underneath the
   * street ring was measured at ~5 ms/frame at retina density. Per CHUNK, not per global
   * radius: a single streaming tile anywhere in view used to collapse the whole cull and
   * pop every hidden chunk back for a frame, which read as buildings flickering with the
   * camera angle. The worker packs far buildings in Morton cell chunks precisely so each
   * sphere here is a compact ~1.2 km patch. Nine samples across the sphere: conservative —
   * a chunk only hides when its whole footprint lands on committed street tiles.
   */
  cullBuildingsCovered(probe: (x: number, z: number) => boolean): void {
    for (const entry of this.#tiles.values()) {
      if (!entry.object) continue;
      const g = entry.object.group;
      const s = g.scale.x;
      for (const child of g.children) {
        if (child.name !== 'buildings' && child.name !== 'roofs') continue;
        if (!(child instanceof Mesh)) continue;
        const sphere = child.geometry.boundingSphere;
        if (!sphere) continue;
        const wx = g.position.x + sphere.center.x * s;
        const wz = g.position.z + sphere.center.z * s;
        const r = sphere.radius * s;
        const d = r * Math.SQRT1_2;
        child.visible = !(
          probe(wx, wz) &&
          probe(wx + r, wz) && probe(wx - r, wz) &&
          probe(wx, wz + r) && probe(wx, wz - r) &&
          probe(wx + d, wz + d) && probe(wx - d, wz + d) &&
          probe(wx + d, wz - d) && probe(wx - d, wz - d)
        );
      }
    }
  }

  /** Does any ready tile intersecting the lon/lat bbox hold building geometry? The survey
   * watcher asks this before queueing a cell — empty desert should never be "surveyed". */
  hasBuildingsWithin(minLon: number, minLat: number, maxLon: number, maxLat: number): boolean {
    for (const entry of this.#tiles.values()) {
      if (entry.state !== 'ready' || !entry.hasBuildings) continue;
      const b = tileBoundsLonLat(entry.coord.z, entry.coord.x, entry.coord.y);
      const outside =
        b.maxLon < minLon || b.minLon > maxLon || b.maxLat < minLat || b.minLat > maxLat;
      if (!outside) return true;
    }
    return false;
  }

  /**
   * Refetch every resident tile that intersects a lon/lat bbox. This is how a finished
   * survey becomes visible: the cells' tiles were cached as empty before the surveyors
   * reported back.
   *
   * Soft swap, not drop: the OLD geometry stays on screen while the replacement travels the
   * whole fetch→decode→build pipeline, and is only torn down the moment the fresh tile
   * starts committing (#commit). Dropping first is what made every survey completion BLINK
   * a patch of city out for seconds before streaming it back — with the player standing
   * right there, because the player being there is what queued the survey.
   */
  invalidateRegion(minLon: number, minLat: number, maxLon: number, maxLat: number): void {
    for (const entry of this.#tiles.values()) {
      const b = tileBoundsLonLat(entry.coord.z, entry.coord.x, entry.coord.y);
      const outside =
        b.maxLon < minLon || b.minLon > maxLon || b.maxLat < minLat || b.minLat > maxLat;
      if (outside) continue;
      entry.gen += 1;
      entry.state = 'loading';
      // An in-flight fetch will notice its gen is stale on arrival and requeue itself.
      if (!entry.inFlight) this.#queue.push(entry.coord);
    }
  }

  #pump(): void {
    while (this.#loading < MAX_CONCURRENT && this.#queue.length > 0) {
      const coord = this.#queue.shift();
      if (!coord) break;
      const key = formatChunkKey(coord.z, coord.x, coord.y);
      const entry = this.#tiles.get(key);
      // The tile may have been evicted between being queued and being pumped — or already
      // be loading (a double invalidation queues the same coord twice).
      if (!entry || entry.state !== 'loading' || entry.inFlight) continue;

      entry.inFlight = true;
      const gen = entry.gen;
      this.#loading += 1;
      const url = this.opts.tileUrl(coord.z, coord.x, coord.y);

      this.opts.pipeline
        .load(coord.z, coord.x, coord.y, url, this.#origin.scale)
        .then((result) => {
          this.#loading -= 1;
          this.#announcedDone += 1;
          this.#progress();

          const live = this.#tiles.get(key);
          if (!live) return; // evicted while in flight; drop the result
          live.inFlight = false;

          // Invalidated while this fetch was in flight: the result predates the survey
          // (and was fetched under the old cache-bust URL). Go again, fresh.
          if (gen !== live.gen) {
            this.#queue.push(live.coord);
            return;
          }

          if (result.meshes.length === 0) {
            // A refetch can come back genuinely empty; whatever the stale tile was
            // showing must not linger under an 'empty' label.
            this.#dropObject(live);
            live.state = 'empty';
            return;
          }
          // Not committed here: the next update() admits a budget's worth per frame.
          this.#arrivals.push({ key, result, gen });
        })
        .catch((failure: unknown) => {
          this.#loading -= 1;
          this.#announcedDone += 1;
          this.#progress();
          const live = this.#tiles.get(key);
          if (live) {
            live.inFlight = false;
            if (gen !== live.gen) {
              this.#queue.push(live.coord);
              return;
            }
            // Keep whatever geometry was already showing: a stale tile beats a hole.
            live.state = 'failed';
          }
          // CLAUDE.md: never a silent catch. One tile failing must not take the map down,
          // so this is logged and the tile is left absent rather than thrown.
          console.warn('[tiles] load failed', key, failure);
        });
    }
  }

  #progress(): void {
    if (this.opts.announce === false) return;
    const total = Math.max(1, this.#announcedTotal);
    emit('boot:progress', {
      phase: 'tiles',
      ratio: Math.min(1, this.#announcedDone / total),
    });
  }

  /**
   * The meshes a click should test against: the two solid layers, from tiles that are actually
   * on screen. Rebuilt per call rather than cached — it changes as tiles stream, it is only
   * asked for on a click, and a stale cache here means clicking a building that is no longer
   * there.
   */
  pickTargets(): Object3D[] {
    const out: Object3D[] = [];
    for (const entry of this.#tiles.values()) {
      if (entry.state !== 'ready' || !entry.object) continue;
      for (const child of entry.object.group.children) {
        if (child.name === 'buildings' || child.name === 'roofs') out.push(child);
      }
    }
    return out;
  }

  #place(entry: TileEntry): void {
    if (!entry.object) return;
    const [mercX, mercY] = tileOriginMerc(entry.coord.z, entry.coord.x, entry.coord.y);
    const [x, z] = mercToLocal(mercX, mercY, this.#origin);
    /*
     * Content was built at `builtScale` metres per mercator metre; positions are spaced at the
     * current origin's scale. Scaling the group by the ratio makes content width equal tile
     * spacing *exactly* — mercWidth × builtScale × (origin/built) = mercWidth × origin — which
     * is what keeps tile edges seamless after the origin has crossed the country.
     */
    setTilePosition(
      entry.object, x, z,
      this.opts.yOffset ?? 0,
      this.#origin.scale / entry.builtScale,
    );
  }

  #evict(visible: TileCoord[]): void {
    if (this.#tiles.size <= this.opts.maxResident) {
      // Under the cap, only drop tiles well outside the visible ring.
      const keep = new Set(visible.map((c) => formatChunkKey(c.z, c.x, c.y)));
      for (const entry of this.#tiles.values()) {
        if (keep.has(entry.key)) continue;
        if (this.#frame - entry.lastSeen < 240) continue; // ~4s of grace
        if (!withinHysteresis(entry.coord, visible)) this.#drop(entry);
      }
      return;
    }

    // Over the cap: evict least-recently-seen first.
    const sorted = [...this.#tiles.values()].sort((a, b) => a.lastSeen - b.lastSeen);
    const excess = this.#tiles.size - this.opts.maxResident;
    const keep = new Set(visible.map((c) => formatChunkKey(c.z, c.x, c.y)));
    let dropped = 0;
    for (const entry of sorted) {
      if (dropped >= excess) break;
      if (keep.has(entry.key)) continue;
      this.#drop(entry);
      dropped += 1;
    }
  }

  /** Tear down an entry's scene geometry (if any) without forgetting the entry itself. */
  #dropObject(entry: TileEntry): void {
    if (!entry.object) return;
    this.opts.scene.remove(entry.object.group);
    entry.object.dispose();
    entry.object = null;
    entry.hasBuildings = false;
    delete entry.paths;
    this.#mutations += 1;
  }

  #drop(entry: TileEntry): void {
    this.#dropObject(entry);
    this.#tiles.delete(entry.key);
  }

  readonly #graveyard: TileObject[] = [];

  /**
   * Drop every resident tile but keep the manager alive. Used when a ring will be invisible
   * for a while (soaring above the street ring, or on the globe) — the memory comes back the
   * moment the ring is next updated, from warm HTTP caches.
   *
   * Objects are DETACHED here but disposed by `reap`, a few per frame: clearing both rings
   * means ~270 geometries, and disposing them all in one go was a 200 ms frame that outlived
   * the fade meant to hide it. The rings are already invisible when this is called, so the
   * only thing deferred is the memory coming back over the next dozen frames.
   */
  clear(): void {
    for (const entry of [...this.#tiles.values()]) {
      if (entry.object) {
        this.opts.scene.remove(entry.object.group);
        this.#graveyard.push(entry.object);
        this.#mutations += 1;
      }
      this.#tiles.delete(entry.key);
    }
    this.#queue.length = 0;
    this.#arrivals.length = 0;
    this.#inProgress = null;
  }

  /** Dispose up to `n` cleared tiles' GPU resources. Call once per frame while idle. */
  reap(n: number): void {
    for (let i = 0; i < n; i += 1) {
      const dead = this.#graveyard.pop();
      if (!dead) return;
      dead.dispose();
    }
  }

  /**
   * Drop whatever is mid-pipeline without touching resident tiles. A ring that has gone
   * dormant (the far ring under 1.1 km, the street ring in its 9–12 km warm band) stops
   * being update()d, so results that were still in flight would otherwise sit in the
   * arrivals queue FOREVER — holding their memory and, worse, wedging the ring's counters:
   * the skyline e2e caught the far ring pinned at ready 0 with twelve arrivals parked,
   * after the fresh-player placement flow yanked the camera to street level mid-stream.
   * Resident tiles stay warm; a dropped result refetches from HTTP cache in moments.
   */
  discardPending(): void {
    if (this.#inProgress) {
      const entry = this.#tiles.get(this.#inProgress.key);
      if (entry) this.#drop(entry);
      this.#inProgress = null;
    }
    const settle = (key: string): void => {
      const entry = this.#tiles.get(key);
      if (!entry || entry.state !== 'loading' || entry.inFlight) return;
      // A soft-swap entry still showing its previous geometry goes back to being that
      // geometry — 'loading' with nothing queued would wedge it forever. A placeholder
      // with nothing on screen is simply forgotten; the next active update() refetches.
      if (entry.object) entry.state = 'ready';
      else this.#tiles.delete(key);
    };
    for (const a of this.#arrivals) settle(a.key);
    this.#arrivals.length = 0;
    for (const coord of this.#queue) settle(formatChunkKey(coord.z, coord.x, coord.y));
    this.#queue.length = 0;
  }

  dispose(): void {
    this.clear();
    this.reap(this.#graveyard.length);
    this.#tiles.clear();
  }
}

/*
 * Adjacent tiles' depthless ground layers overlap by their clip buffer (~1 m), and with
 * equal renderOrder the winner fell to three's depth sort — which flips as the camera moves,
 * so the overlap strips flickered between the two tiles' colours mid-WASD. A small
 * deterministic per-tile offset makes the winner permanent. Coefficients chosen so all four
 * neighbour relations (±x, ±y, both diagonals) differ mod 16, and the 0–7.5 range never
 * crosses the 10-unit gap between the landuse and water stages (tileMesh FLAT_ORDER) or the
 * ±100 biases separating the rings.
 */
function flatTieBreak(coord: TileCoord): number {
  return ((coord.x * 5 + coord.y * 3) & 15) * 0.5;
}

function withinHysteresis(coord: TileCoord, visible: TileCoord[]): boolean {
  for (const v of visible) {
    if (
      Math.abs(v.x - coord.x) <= HYSTERESIS_TILES &&
      Math.abs(v.y - coord.y) <= HYSTERESIS_TILES
    ) {
      return true;
    }
  }
  return false;
}

const NDC_CORNERS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1], [1, -1], [1, 1], [-1, 1],
];

/**
 * The set of tiles the camera can currently see.
 *
 * Intersects the four frustum corner rays with the ground plane. Rays that point at or above
 * the horizon never hit it — at a shallow pitch two of them always do — so those are clamped
 * to a maximum view distance instead. Without that clamp a near-horizontal camera produces an
 * unbounded ground bbox and the manager tries to load the planet.
 */
export function visibleTiles(
  camera: PerspectiveCamera,
  origin: Origin,
  zoom: number,
  maxView: number = Math.max(2000, camera.far * 0.6),
  maxResident = 96,
  biasX = 0,
  biasZ = 0,
): TileCoord[] {
  const eye = camera.position;

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  // `bias` is a look-ahead: the caller passes the camera's smoothed velocity so the bbox
  // stretches toward where the player is HEADING. Tiles ahead are streaming before they come
  // on screen, which is the difference between "already loaded" and watching pop-in.
  const include = (x: number, z: number): void => {
    minX = Math.min(minX, x, x + biasX);
    maxX = Math.max(maxX, x, x + biasX);
    minZ = Math.min(minZ, z, z + biasZ);
    maxZ = Math.max(maxZ, z, z + biasZ);
  };

  const scratch = new Vector3();
  for (const [nx, ny] of NDC_CORNERS) {
    scratch.set(nx, ny, 0.5).unproject(camera);
    const dir = scratch.sub(eye).normalize();

    if (dir.y < -1e-4) {
      const t = -eye.y / dir.y;
      if (t > 0 && t < maxView) {
        include(eye.x + dir.x * t, eye.z + dir.z * t);
        continue;
      }
    }
    // Above the horizon, or past the clamp: project the horizontal direction out to maxView.
    const horiz = Math.hypot(dir.x, dir.z) || 1;
    include(eye.x + (dir.x / horiz) * maxView, eye.z + (dir.z / horiz) * maxView);
  }
  include(eye.x, eye.z);

  const [wMercX, nMercY] = localToMerc(minX, minZ, origin);
  const [eMercX, sMercY] = localToMerc(maxX, maxZ, origin);
  const [westLon, northLat] = unproject(wMercX, nMercY);
  const [eastLon, southLat] = unproject(eMercX, sMercY);

  const nw = lonLatToTile(westLon, northLat, zoom);
  const se = lonLatToTile(eastLon, southLat, zoom);

  const out: TileCoord[] = [];
  const n = 2 ** zoom;
  const x0 = Math.max(0, Math.min(nw.x, se.x));
  const x1 = Math.min(n - 1, Math.max(nw.x, se.x));
  const y0 = Math.max(0, Math.min(nw.y, se.y));
  const y1 = Math.min(n - 1, Math.max(nw.y, se.y));

  // A degenerate camera pose can still produce a huge span; cap it rather than emitting
  // thousands of requests.
  if ((x1 - x0 + 1) * (y1 - y0 + 1) > maxResident) {
    const cx = Math.floor((x0 + x1) / 2);
    const cy = Math.floor((y0 + y1) / 2);
    const r = Math.floor(Math.sqrt(maxResident) / 2);
    for (let y = cy - r; y <= cy + r; y += 1) {
      for (let x = cx - r; x <= cx + r; x += 1) {
        if (x >= 0 && y >= 0 && x < n && y < n) out.push({ z: zoom, x, y });
      }
    }
    return out;
  }

  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) out.push({ z: zoom, x, y });
  }
  return out;
}
